import {
    Colors,
    DATE_FORMATS,
    FOCUS_RING,
    formatDate,
    Icon,
    ModalDialog,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, PlusIcon } from '../../../shared/PrepareMeetingIcons';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { ProficiencyLevelBars } from '@/components/ProficiencyIcon/ProficiencyLevelBars';
import {
    getProficiencyLevelRank,
    resolveRankedProficiencyLevelColor,
} from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { useSelectedAppLanguage } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevels, orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

interface CompetencyEntry {
    id: string;
    name: string;
    description: string;
    assessedScore: number;
    groupName: string;
    proficiencyLevelId?: string;
    lastAssessedAt: string | null;
}

type SortField = 'name' | 'lastAssessedAt' | 'proficiency';

interface SortToggleProps {
    label: string;
    isActive: boolean;
    sortOrder: 'asc' | 'desc';
    onClick: () => void;
}

/** One sort criterion: pressed while it drives the order, its arrow telling the direction. */
const SortToggle = ({ label, isActive, sortOrder, onClick }: SortToggleProps) => (
    <button
        type={'button'}
        aria-pressed={isActive}
        onClick={onClick}
        className={`${FOCUS_RING} text-caption flex items-center gap-2 px-3 py-1.5 font-medium transition-colors ${
            isActive
                ? 'bg-secondary hover:bg-secondary/90 text-white'
                : 'border-base-100 text-base-600 hover:bg-base-25 border bg-white'
        }`}
    >
        {label}
        {isActive && (
            <Icon
                name={sortOrder === 'asc' ? 'arrow-arrow-up' : 'arrow-arrow-down-1'}
                size={14}
                color={'inherit'}
                ariaHidden={true}
            />
        )}
    </button>
);

interface CompetencySelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onInsert: (competency: CompetencyEntry) => void;
    masteredCompetencies: CompetencyEntry[];
    growingCompetencies: CompetencyEntry[];
    availableCompetencies: CompetencyEntry[];
    proficiencyLevels: ProficiencyLevel[];
    modalSectionType: 'Mastered' | 'Growth';
}

export const CompetencySelectionModal = ({
    isOpen,
    onClose,
    onInsert,
    masteredCompetencies,
    growingCompetencies,
    availableCompetencies,
    proficiencyLevels,
}: CompetencySelectionModalProps) => {
    const { t } = useTranslation();

    const selectedLanguage = useSelectedAppLanguage();
    const [sortBy, setSortBy] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const levelById = useMemo(
        () => new Map(proficiencyLevels.map((level) => [level.id ?? '', level])),
        [proficiencyLevels],
    );

    // The footer legend is a row, and a row is read left to right, so it ends on the best level.
    // Sorting the rows by proficiency reads `sortOrder` directly and is unaffected.
    const legendLevels = orderProficiencyLevelsWorstFirst(proficiencyLevels);
    const rankedLevels = orderProficiencyLevels(proficiencyLevels);

    const getLevel = (competency: CompetencyEntry) =>
        competency.proficiencyLevelId ? levelById.get(competency.proficiencyLevelId) : undefined;

    // Rank order, best level first: what both the bars and the colour resolver count from.
    const orderedLevelIds = rankedLevels.map((level) => level.id);

    const getLevelIndex = (level: ProficiencyLevel) =>
        getProficiencyLevelRank({ level, orderedLevelIds });

    const getLevelColor = (level: ProficiencyLevel) =>
        resolveRankedProficiencyLevelColor({ level, orderedLevelIds });

    // A competency can only sit on one list: it counts as added when it is on EITHER list, whichever list the
    // modal was opened from.
    const isCompetencyAdded = (competencyId: string) =>
        masteredCompetencies.some((c) => c.id === competencyId) ||
        growingCompetencies.some((c) => c.id === competencyId);

    const parseDate = (date: string | null) => {
        if (!date) return Number.POSITIVE_INFINITY;
        const time = new Date(date).getTime();
        return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
    };

    const sortedCompetencies = [...availableCompetencies].sort((a, b) => {
        let comparison = 0;
        if (sortBy === 'name') {
            comparison = a.name.localeCompare(b.name);
        } else if (sortBy === 'lastAssessedAt') {
            const aTime = parseDate(a.lastAssessedAt);
            const bTime = parseDate(b.lastAssessedAt);
            if (aTime === Number.POSITIVE_INFINITY && bTime === Number.POSITIVE_INFINITY) {
                return 0;
            }
            if (aTime === Number.POSITIVE_INFINITY) {
                return 1;
            }
            if (bTime === Number.POSITIVE_INFINITY) {
                return -1;
            }
            return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
        } else if (sortBy === 'proficiency') {
            const levelA = getLevel(a);
            const levelB = getLevel(b);
            comparison =
                (levelA?.sortOrder ?? Number.MAX_SAFE_INTEGER) -
                (levelB?.sortOrder ?? Number.MAX_SAFE_INTEGER);
        }
        return sortOrder === 'asc' ? comparison : -comparison;
    });

    const handleToggleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const sortFields: { field: SortField; label: string }[] = [
        { field: 'name', label: t('postReviewPrepareMeeting.competencies.name') },
        { field: 'lastAssessedAt', label: t('postReviewPrepareMeeting.competencies.date') },
        { field: 'proficiency', label: t('postReviewPrepareMeeting.competencies.proficiency') },
    ];

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('postReviewPrepareMeeting.competencies.selectCompetency')}
            subtitle={t('postReviewPrepareMeeting.competencies.chooseCompetency')}
            size={'lg'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={'competency-selection-modal'}
            headerExtra={
                <div className={'flex flex-wrap items-center justify-between gap-3'}>
                    <span
                        className={
                            'text-body-sm flex items-center gap-2 font-semibold text-gray-800'
                        }
                    >
                        <Icon
                            name={'arrow-arrow-3'}
                            size={16}
                            color={Colors.BASE_600}
                            ariaHidden={true}
                        />
                        {t('postReviewPrepareMeeting.competencies.sortBy')}:
                    </span>

                    <div className={'flex gap-2'}>
                        {sortFields.map(({ field, label }) => (
                            <SortToggle
                                key={field}
                                label={label}
                                isActive={sortBy === field}
                                sortOrder={sortOrder}
                                onClick={() => handleToggleSort(field)}
                            />
                        ))}
                    </div>
                </div>
            }
            footer={
                <div className={'flex flex-col gap-3'}>
                    <span className={'text-navy text-body-sm font-semibold'}>
                        {t('postReviewPrepareMeeting.competencies.proficiencyLevels')}
                    </span>

                    <ul
                        className={'flex flex-wrap gap-6'}
                        data-testid={'competency-selection-legend'}
                    >
                        {legendLevels.map((level, index) => (
                            <li
                                key={`${level?.id}-${index}`}
                                className={'flex items-center gap-3'}
                            >
                                <span className={'flex size-6 items-center justify-center'}>
                                    <ProficiencyIcon
                                        iconEntry={getIconEntry(level?.icon || '')}
                                        iconUrl={level?.iconUrl}
                                        color={getLevelColor(level)}
                                    />
                                </span>
                                <span className={'text-body-sm text-gray-900'}>
                                    {getLocalizedText(level?.label ?? '')}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            }
        >
            <div className={'divide-base-100 divide-y'}>
                {sortedCompetencies.map((competency) => {
                    const competencyLevel = getLevel(competency);

                    return (
                        <div
                            key={competency.id}
                            className={
                                'group hover:bg-base-25 overflow-hidden rounded-lg bg-white px-3 py-4 transition-all'
                            }
                        >
                            <div className={'flex justify-between gap-4'}>
                                <div className={'flex flex-1 items-start gap-4'}>
                                    <div
                                        className={
                                            'flex flex-shrink-0 flex-col items-center gap-1 pt-1.5'
                                        }
                                    >
                                        <div className={'flex size-6 items-center justify-center'}>
                                            {competencyLevel ? (
                                                <ProficiencyIcon
                                                    iconEntry={getIconEntry(competencyLevel.icon)}
                                                    iconUrl={competencyLevel.iconUrl}
                                                    color={getLevelColor(competencyLevel)}
                                                />
                                            ) : (
                                                <Icon
                                                    name={'essential-minus'}
                                                    color={Colors.BASE_400}
                                                    size={20}
                                                    ariaHidden={true}
                                                />
                                            )}
                                        </div>
                                        {competencyLevel && (
                                            <ProficiencyLevelBars
                                                levelIndex={getLevelIndex(competencyLevel)}
                                                totalLevels={rankedLevels.length}
                                                color={getLevelColor(competencyLevel)}
                                                dataTestId={`competency-selection-${competency.id}-proficiency-bars`}
                                            />
                                        )}
                                    </div>
                                    <div className={'min-w-0 flex-1'}>
                                        <h4
                                            id={`competency-name-${competency.id}`}
                                            className={'text-body-sm text-navy mb-1 font-semibold'}
                                        >
                                            {competency.name}
                                        </h4>
                                        <p className={'text-caption text-base-600 leading-relaxed'}>
                                            {competency.description ||
                                                t(
                                                    'postReviewPrepareMeeting.competencies.defaultDescription',
                                                )}
                                        </p>
                                    </div>
                                </div>

                                <div className={'flex flex-shrink-0 items-center gap-4'}>
                                    {competency.lastAssessedAt && (
                                        <span
                                            className={
                                                'text-caption text-base-600 flex items-center gap-1 whitespace-nowrap'
                                            }
                                        >
                                            <Icon
                                                name={'time-clock'}
                                                color={Colors.BASE_400}
                                                size={16}
                                                ariaHidden={true}
                                            />
                                            {formatDate({
                                                date: competency.lastAssessedAt,
                                                format: DATE_FORMATS.DISPLAY_DATE,
                                                language: selectedLanguage,
                                            })}
                                        </span>
                                    )}
                                    {isCompetencyAdded(competency.id) ? (
                                        <div
                                            className={
                                                'text-font-xs bg-base-500 flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-semibold whitespace-nowrap text-white'
                                            }
                                        >
                                            <CheckIcon
                                                className={'size-3.5'}
                                                strokeWidth={2}
                                            />
                                            {t('postReviewPrepareMeeting.competencies.added')}
                                        </div>
                                    ) : (
                                        <button
                                            type={'button'}
                                            onClick={() => onInsert(competency)}
                                            aria-describedby={`competency-name-${competency.id}`}
                                            // Revealed on row hover, and whenever keyboard focus is inside the row.
                                            className={`${FOCUS_RING} text-font-xs bg-secondary hover:bg-secondary/90 flex items-center gap-1.5 px-3 py-1.5 font-semibold whitespace-nowrap text-white opacity-0 transition-all duration-200 group-focus-within:opacity-100 group-hover:opacity-100`}
                                        >
                                            <PlusIcon
                                                className={'size-3.5'}
                                                strokeWidth={2}
                                            />
                                            {t('postReviewPrepareMeeting.competencies.insert')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModalDialog>
    );
};
